import { useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import { useDispatch } from '../../../Flux/index.ts'
import { orderWizardActions as wizard } from '../../../Flux/Slice/OrderWizard/OrderWizardSlice.ts'
import type { DraftState, WizardStep } from '../../../Model/OrderWizard/OrderWizardDraft.ts'
import { detectionSettled, firstAttentionStep, firstIssue, STEP_LABELS } from '../../../Services/OrderWizard/issues.ts'
import { selectCurrentStep, selectDraftIssues, selectVisibleSteps } from '../../../Flux/Selector/OrderWizard/OrderWizardSelectors.ts'
import { useStableCallback } from '../../../Framework/View/Hooks/useLatest.ts'
import { WizardStepper } from './WizardStepper.tsx'
import { StepIssues, showTarget } from './StepIssues.tsx'
import { RetryFailedUploads } from './WizardFileList.tsx'
import { ReverseMissingActions } from './ReverseFiles.tsx'
import { REVERSE_MISSING } from '../../../Services/OrderWizard/issues.ts'
import { WizardQuestions } from './WizardQuestions.tsx'
import { stepLeadsWithQuestion } from '../../../Services/OrderWizard/questions.ts'
import type { SelectStep } from './stepSelect.ts'
import { StepSelectContext } from './stepSelect.ts'
import { LeavePrompt } from './LeavePrompt.tsx'
import { StepFooter } from './StepFooter.tsx'
import { StepBody } from './StepBody.tsx'
import { PrimerPasteContext } from './primerPasteRequest.ts'
import type { LeaveOrigin } from './PendingPoolEdit.tsx'
import { PendingPoolEditContextProvider, usePendingPoolEditState } from './PendingPoolEdit.tsx'

/** What the lab does on each step, in one sentence, where the step's own content does not already say it. */
const STEP_LEADS: Partial<Record<WizardStep, string>> = {
  files: 'Check what was read from your files; drop more files here at any time.',
  primers: 'Check the exact primers, tag included, of each sample.',
  metadata: 'Check how the sample sheet’s columns are imported.',
  review: 'The final checks run on their own; once they pass, submit the order to processing.'
}

/**
 * The order, one topic per step. Each step shows its own issues and question cards; when it has none it
 * is a line to glance at and confirm. Uploads and writes carry on whatever step is open.
 */
export const WizardSteps = ({
  draft,
  orderId,
  dropzone,
  header
}: {
  draft: DraftState
  orderId: string
  dropzone: ReactNode
  /** The page's global bar, framed together with the stepper. */
  header?: ReactNode
}) => {
  const dispatch = useDispatch()
  const issues = selectDraftIssues(draft)
  const steps = selectVisibleSteps(draft)
  const step = selectCurrentStep(draft)
  const settled = detectionSettled(draft)
  const poolEdit = usePendingPoolEditState(draft, step === 'pools', `${draft.id}|${step}`)
  const bodyLeads = stepLeadsWithQuestion(draft, step) || poolEdit.pending
  const [pasteRequests, setPasteRequests] = useState(0)
  const requestPaste = useStableCallback(() => setPasteRequests((count) => count + 1))

  useEffect(() => {
    if (!draft.step && settled) {
      dispatch(wizard.stepChanged({ draftId: draft.id, step: firstAttentionStep(issues, steps) }))
    }
  }, [draft.step, draft.id, settled, issues, steps, dispatch])

  const go: SelectStep = (next, before) => {
    before?.()
    dispatch(wizard.stepChanged({ draftId: draft.id, step: next }))
    window.scrollTo?.({ top: 0, behavior: 'smooth' })
  }
  const selectFrom =
    (from: LeaveOrigin): SelectStep =>
    (next, before) =>
      next !== step ? poolEdit.guard(() => go(next, before), from) : go(next, before)
  const selectFromStepper = useStableCallback(selectFrom('stepper'))
  const selectFromFooter = useStableCallback(selectFrom('footer'))
  const prompt = (from: LeaveOrigin) =>
    poolEdit.leaving === from ? (
      <LeavePrompt onApply={() => poolEdit.resolveLeave(true)} onDiscard={() => poolEdit.resolveLeave(false)} onStay={poolEdit.stay} />
    ) : undefined
  const showIssues = useStableCallback(() => {
    const first = firstIssue(issues)
    if (first) {
      dispatch(wizard.stepChanged({ draftId: draft.id, step: first.step }))
      showTarget(first.target)
    }
  })

  return (
    <PendingPoolEditContextProvider value={poolEdit}>
      <StepSelectContext.Provider value={selectFromStepper}>
        <PrimerPasteContext.Provider value={requestPaste}>
          <div className="space-y-3" data-testid="wizard-steps">
            <div className="rounded-xl border border-slate-200 bg-white">
              {header && <div className="border-b border-slate-100 px-4 py-2 sm:px-6">{header}</div>}
              <div className="space-y-2 px-4 py-3 sm:px-6">
                <WizardStepper steps={steps} current={step} issues={issues} onSelect={selectFromStepper} />
                {prompt('stepper')}
              </div>
            </div>
            <section className="rounded-xl border border-slate-200 bg-white" data-testid={`wizard-step-body-${step}`}>
              <div className="space-y-3 p-4 sm:px-6">
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">{STEP_LABELS[step]}</h2>
                  {STEP_LEADS[step] && (
                    <p className="text-sm text-slate-500" data-testid="wizard-step-lead">
                      {STEP_LEADS[step]}
                    </p>
                  )}
                </div>
                <StepIssues
                  issues={issues}
                  step={step}
                  actions={{
                    'uploads-broken': <RetryFailedUploads draft={draft} />,
                    [REVERSE_MISSING]: <ReverseMissingActions draft={draft} />
                  }}
                />
                <WizardQuestions draft={draft} step={step} />
                <StepBody
                  draft={draft}
                  orderId={orderId}
                  step={step}
                  issues={issues}
                  onShowIssues={showIssues}
                  dropzone={dropzone}
                  pasteRequests={pasteRequests}
                />
              </div>
              <StepFooter
                draft={draft}
                step={step}
                steps={steps}
                issues={issues}
                primary={!bodyLeads}
                prompt={prompt('footer')}
                onSelect={selectFromFooter}
                onShowIssues={showIssues}
              />
            </section>
          </div>
        </PrimerPasteContext.Provider>
      </StepSelectContext.Provider>
    </PendingPoolEditContextProvider>
  )
}
